import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { Product } from '@shared/models/product';
import { BcmDynamicPrice, BcmDynamicPriceContext } from '@shared/models/bcm-dynamic-price';
import { Boat } from '@shared/models/boat';
import { Berth } from '@shared/models/berth';
import { TenantRelation } from '@shared/models/tenant-relation';
import { Season } from '@shared/models/seasons';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { ProductService } from '@modules/bcm/products/product/product.service';
import { BcmProductDynamicPriceMap } from '@shared/models/bcm-price-rule-map';
import { WinterStorage } from '@shared/models/winter-storage';
import { SeasonsService } from '@modules/bcm/seasons/seasons.service';
import { Pier } from '@shared/models/pier';
import { BerthBoatAssignment } from '@shared/models/berth-boat-assignment';
import { DurationUnit, getDurationFromDates } from '@shared/functions/get-duration-from-dates';
import { DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';
import { Person } from '@shared/models/person';
import { Company } from '@shared/models/company';
import {
    DynamicPriceState,
    hasEvaluableRules,
    resolveDynamicPriceState,
} from '@shared/functions/resolve-dynamic-price-state';
import { ProductsApiService } from '@modules/bcm/@shared/services';

@Component({
    selector: 'product-dynamic-price',
    templateUrl: './product-dynamic-price.component.html',
    styleUrls: ['./product-dynamic-price.component.scss'],
    standalone: false
})
export class ProductDynamicPriceComponent implements OnInit, OnDestroy, OnChanges {

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private _person$ = new ReplaySubject<Person | null>(1);
    private _company$ = new ReplaySubject<Company | null>(1);

    readonly person$: Observable<Person | null> = this._person$.asObservable();
    readonly company$: Observable<Company | null> = this._company$.asObservable();

    @Input() parentFormGroup: UntypedFormGroup;

    @Input() dynamicPrice: BcmDynamicPrice;

    @Input() dynamicPriceContext: BcmDynamicPriceContext;

    @Input() product: Product;

    @Input() berthAssignments: BerthBoatAssignment[];

    @Input()
    useDynamicPrice: boolean;

    @Input()
    defaultBerthReservationTimeUnit: DefaultBerthReservationTimeUnit;

    @Output()
    dynamicPriceChanged: EventEmitter<BcmDynamicPrice> = new EventEmitter<BcmDynamicPrice>();

    @Output()
    dynamicPriceErrorChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output()
    useDynamicPriceChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

    dynamicPriceOld: BcmDynamicPrice;

    boats: Boat[] = [];
    berths: Berth[] = [];
    tenantRelations: TenantRelation[] = [];
    seasons: Season[] = [];
    winterStorages: WinterStorage[] = [];
    piers: Pier[] = [];

    boat: Boat;
    berth: Berth;
    tenantRelation: TenantRelation;
    season: Season;
    winterStorage: WinterStorage;
    pier: Pier;

    requirements: string[] = [];

    /**
     * Requirement ids this component renders a selector for. Rules and calculations may also name
     * `product`, `vestingPeriod` or `travelers` - those come from the position itself, so they must
     * not be mistaken for "nothing to ask the user about". (#6944)
     */
    private static readonly CRITERIA_WIDGET_IDS = [
        'boat', 'berth', 'tenantRelation', 'season', 'winterStorage', 'pier',
    ];

    /** Product ids already refetched, so a product without rules is not requested again and again. */
    private _rulesRefetchedFor = new Set<number>();

    /** Whether a rule map refetch is in flight - suppresses every message until it settles. */
    private _loadingRules = false;

    /** Whether an evaluation has come back - gates the "no rule matched" message. */
    private _evaluated = false;

    /** Set when the rule engine itself failed, which is not the same as "no rule matched". */
    private _evaluationFailed = false;

    /**
     * Relations the current product's price rules reference. Empty means the rules name no specific
     * relations, so the selector stays unrestricted.
     */
    relevantTenantRelationIds: number[] = [];

    formGroup: UntypedFormGroup;

    init = false;

    parentTitle: string;

    /** Zuletzt von dieser Komponente in das Titelfeld geschriebener Regelname. */
    private _lastAppliedRuleName: string;

    seasonChangedManually: boolean;

    initIndexChanged: boolean;
    initTitleChanged: boolean;
    initPriceChanged: boolean;

    constructor(private _formBuilder: FormBuilder,
                private _changeDetectorRef: ChangeDetectorRef,
                private _seasonsService: SeasonsService,
                private _productsApiService: ProductsApiService,
                private _productService: ProductService) {
    }

    /** Whether any requirement has a selector to render - drives the criteria block. */
    get hasCriteriaWidgets(): boolean {
        return (this.requirements || [])
            .some(requirement => ProductDynamicPriceComponent.CRITERIA_WIDGET_IDS.includes(requirement));
    }

    /**
     * Translation key explaining why the panel has no price to show, or null while it has one.
     * Both messages are held back while the rule map or the first evaluation is still in flight -
     * otherwise a product whose rules are on their way is reported as having none, telling the user
     * to go fix a product that is fine. (#6944)
     */
    get stateMessageKey(): string | null {
        if (this._loadingRules) {
            return null;
        }

        if (this._evaluationFailed) {
            return 'workflows.screens.productDynamicPrice.evaluationFailed';
        }

        switch (resolveDynamicPriceState(this.product, this.dynamicPrice)) {
            case DynamicPriceState.NoRules:
                return 'workflows.screens.productDynamicPrice.noRulesConfigured';
            case DynamicPriceState.NoMatch:
                return this._evaluated ? 'workflows.screens.productDynamicPrice.noRuleMatched' : null;
            default:
                return null;
        }
    }

    ngOnInit(): void {
        this._createForm();

        this._processContext();

        this._resolveRequirements();

        this._ensureRulesLoaded();

        // Der mitgebrachte Regelname zählt als automatisch abgeleitet, damit die Bezeichnung dem
        // Regelwechsel weiter folgt. Nicht über dynamicPriceOld: das hängt an `ruleIndex >= 0`,
        // was für v2 (ruleIndex null) nur zufällig wahr ist. (#6990)
        this._lastAppliedRuleName = this.dynamicPrice?.ruleName;

        const hasStoredDynamicPrice = this.dynamicPrice?.id >= 0 || this.dynamicPrice?.ruleIndex >= 0;

        if (hasStoredDynamicPrice) {
            this.dynamicPriceOld = {...this.dynamicPrice};
            this.dynamicPrice = new BcmDynamicPrice({id: this.dynamicPrice.id});
        }

        // A new position has no stored price to re-check, and without a criteria widget nothing ever
        // registers a sub-form - so `formGroup.valueChanges` never fires and the rules stay
        // unevaluated. Evaluate once here too, so the panel knows what it has. (#6944)
        if (hasStoredDynamicPrice || this.useDynamicPrice) {
            // ExpressionChangedAfterItHasBeenCheckedError fix
            Promise.resolve(null).then(async () => {
                await this._validateRules();
            });
        }

        setTimeout(() => {
            this.formGroup
                .valueChanges
                .pipe(
                    distinctUntilChanged(),
                    takeUntil(this._unsubscribeAll)
                )
                .subscribe(async values => {

                    this.boat = values.boatForm?.boat;
                    this.berth = values.berthForm?.berth;
                    this.tenantRelation = values.tenantRelationForm?.tenantRelation;
                    this.season = values.seasonForm?.season;
                    this.winterStorage = values.winterStorageForm?.winterStorage;
                    this.pier = values.pierForm?.pier;

                    this._checkRequirements();

                    this.useDynamicPriceChanged.next(values.useDynamicPrice);

                    if (values.useDynamicPrice) {
                        await this._validateRules();

                        if (!this.useDynamicPrice) {
                            this._setDefaultValues();
                        }
                    }

                    this._changeDetectorRef.detectChanges();

                });
        }, 1000);

        this._checkRequirements();
        this._changeDetectorRef.detectChanges();

        if (!this.dynamicPrice?.id) {
            this.init = true;
        }

    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
        this._person$.complete();
        this._company$.complete();
        this.parentFormGroup.removeControl('dynamicPriceFormGroup');
    }

    ngOnChanges(changes: SimpleChanges): void {
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName)) {
                switch (propName) {
                    case 'product': {
                        // ngOnInit derives the criteria for the initial product — and it runs after
                        // this, so there would be no form to check against yet.
                        if (changes['product'].firstChange) {
                            break;
                        }

                        // Selecting another product in an open dialog does not recreate this
                        // component, so the criteria have to be re-derived from the new rule map.
                        this._resolveRequirements();
                        this._ensureRulesLoaded();

                        // _checkRequirements() emits dynamicPriceErrorChanged, which the dialog binds
                        // to a field its template has already read this pass — emit after it, like the
                        // other emit sites below.
                        Promise.resolve(null).then(() => this._checkRequirements());

                        if (this.useDynamicPrice) {
                            setTimeout(() => {
                                // ExpressionChangedAfterItHasBeenCheckedError fix
                                Promise.resolve(null).then(async () => {
                                    await this._validateRules();
                                });
                                setTimeout(() => this.init = true, 100);
                            }, 250);
                        }
                        break;
                    }
                    case 'dynamicPriceContext': {

                        const previousBerth = changes['dynamicPriceContext'].previousValue?.preselection?.berth?.id;
                        const currentBerth = changes['dynamicPriceContext'].currentValue?.preselection?.berth?.id;

                        const berthChanged = previousBerth !== currentBerth;

                        if (berthChanged) {
                            this._processContextBerth();
                        }

                        const prevPersonId = changes['dynamicPriceContext'].previousValue?.person?.id;
                        const currPersonId = changes['dynamicPriceContext'].currentValue?.person?.id;
                        if (prevPersonId !== currPersonId) {
                            this._person$.next(this.dynamicPriceContext?.person || null);
                        }

                        const prevCompanyId = changes['dynamicPriceContext'].previousValue?.company?.id;
                        const currCompanyId = changes['dynamicPriceContext'].currentValue?.company?.id;
                        if (prevCompanyId !== currCompanyId) {
                            this._company$.next(this.dynamicPriceContext?.company || null);
                        }

                        const fromDateChanged = changes['dynamicPriceContext'].previousValue?.fromDate !== changes['dynamicPriceContext'].currentValue?.fromDate;
                        const toDateChanged = changes['dynamicPriceContext'].previousValue?.toDate !== changes['dynamicPriceContext'].currentValue?.toDate;

                        if ((!this.seasonChangedManually || !this.formGroup.get('seasonForm')?.get('season')?.value?.id) && (fromDateChanged || toDateChanged)) {
                            this._processContextSeason();
                            this.seasonChangedManually = false;
                        }

                        if (this.useDynamicPrice) {
                            setTimeout(() => {
                                // ExpressionChangedAfterItHasBeenCheckedError fix
                                Promise.resolve(null).then(async () => {
                                    await this._validateRules();
                                });
                                setTimeout(() => this.init = true, 100);
                            }, 250);
                        }

                        break;
                    }
                }
            }
        }
    }

    private _createForm(): void {
        this.formGroup = this._formBuilder.group({
            useDynamicPrice: [this.useDynamicPrice],
            dynamicPrice: []
        });

        this.formGroup.get('useDynamicPrice').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                this.useDynamicPrice = value;
                if (!value) {
                    this.formGroup.get('dynamicPrice').setValue(null);

                    if (this.parentTitle && this.parentFormGroup?.get('title')) {
                        this.parentFormGroup.get('title').setValue(this.parentTitle);
                    }
                } else {
                    this.formGroup.get('dynamicPrice').setValue(this.dynamicPrice);
                }
            });

        this.parentFormGroup.addControl('dynamicPriceFormGroup', this.formGroup);

        // Not every host has a title to restore - the contract creator's product rows carry only
        // product/quantity/price. (#6959)
        this.parentTitle = this.parentFormGroup.get('title')?.value;

        this.parentFormGroup.get('quantity')?.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async () => {
                if (this.formGroup.get('useDynamicPrice').value) {
                    await this._validateRules();
                }
            });
    }

    private _resolveRequirements(): void {
        try { // TODO find bug .. (thilo: @pascal: which bug?)
            this.requirements = this.product?.priceRuleMap?.getRequirements() || [];
        } catch (e) {
            // priceRuleMap arrived as a plain object instead of a model instance.
            this.product.priceRuleMap = new BcmProductDynamicPriceMap({...this.product?.priceRuleMap});
            this.requirements = this.product?.priceRuleMap?.getRequirements() || [];
        }

        this.relevantTenantRelationIds = this.product?.priceRuleMap?.getTenantRelationCriteriaIds() || [];
    }

    /**
     * A product flagged `hasDynamicPrice` whose `priceRuleMap` never arrived looks exactly like one
     * without any rules: no criteria, no evaluation, no price. That has bitten several entry points
     * already (#6756, #6855, #6922), so rather than trust every caller to fetch the full product,
     * fetch it here once when the relation is missing. (#6944)
     */
    private _ensureRulesLoaded(): void {
        const productId = this.product?.id;

        if (!productId
            || !this.product?.hasDynamicPrice
            || this.product?.priceRuleMap != null
            || this._rulesRefetchedFor.has(productId)) {
            return;
        }

        this._rulesRefetchedFor.add(productId);
        this._loadingRules = true;

        this._productsApiService
            .getOne(productId)
            .pipe(take(1), takeUntil(this._unsubscribeAll))
            .subscribe(async product => {
                this._loadingRules = false;

                // The dialog may have moved on to another product while this was in flight.
                if (this.product?.id !== productId) {
                    return;
                }

                this.product.priceRuleMap = product?.priceRuleMap;
                this._resolveRequirements();
                this._checkRequirements();

                if (this.useDynamicPrice) {
                    await this._validateRules();
                }

                this._changeDetectorRef.detectChanges();
            }, () => {
                // Forget the id again: a latched failure would leave the panel claiming forever that
                // a product with rules has none, with no way to retry.
                this._loadingRules = false;
                this._rulesRefetchedFor.delete(productId);
                this._changeDetectorRef.detectChanges();
            });
    }

    private _checkRequirements(): void {
        const values = this.formGroup?.getRawValue();

        if (!values) {
            return;
        }

        this.dynamicPriceErrorChanged.next(false);

        let missingReason: string | null = null;
        if (values.useDynamicPrice && this.requirements?.length > 0) {
            if (this.requirements.includes('boat') && (!values.boatForm?.boat?.id && !this.boat?.id)) {
                missingReason = 'boat';
            } else if (this.requirements.includes('berth') && (!values.berthForm?.berth?.id && !this.berth?.id)) {
                missingReason = 'berth';
            } else if (this.requirements.includes('tenantRelation') && (!values.tenantRelationForm?.tenantRelation?.id && !this.tenantRelation?.id)) {
                missingReason = 'tenantRelation';
            } else if (this.requirements.includes('season') && (!values.seasonForm?.season?.id && !this.season?.id)) {
                missingReason = 'season';
            } else if (this.requirements.includes('winterStorage') && (!values.winterStorageForm?.winterStorage?.id && !this.winterStorage?.id)) {
                missingReason = 'winterStorage';
            } else if (this.requirements.includes('pier') && (!values.pierForm?.pier?.id && !this.pier?.id)) {
                missingReason = 'pier';
            }
            if (missingReason) {
                this.dynamicPriceErrorChanged.next(true);
            }
        }
    }

    private _processContext(): void {
        this._person$.next(this.dynamicPriceContext?.person || null);
        this._company$.next(this.dynamicPriceContext?.company || null);

        this.boats = (this.dynamicPriceContext?.person?.boats || this.dynamicPriceContext?.company?.boats) || [];

        if (this.dynamicPriceContext?.preselection?.boat?.id &&
            (this.boats.length === 0 || this.boats.findIndex(b => b.id === this.dynamicPriceContext?.preselection.boat.id) === -1)) {
            this.boats.push(this.dynamicPriceContext?.preselection?.boat);
        }

        this.tenantRelations = (
            this.dynamicPriceContext?.person?.activeTenantRelationAssignments
            || this.dynamicPriceContext?.company?.activeTenantRelationAssignments
            || []
        ).map(tenantRelationAssignment => tenantRelationAssignment.tenantRelation) || [];

        if (this.dynamicPriceContext?.preselection?.tenantRelation?.id &&
            (this.tenantRelations.length === 0 || this.tenantRelations.findIndex(t => t.id === this.dynamicPriceContext?.preselection.tenantRelation.id) === -1)) {
            this.tenantRelations.push(this.dynamicPriceContext?.preselection?.tenantRelation);
        }

        this.winterStorages = this.dynamicPriceContext?.preselection?.winterStorage?.id ? [this.dynamicPriceContext?.preselection?.winterStorage] : [];

        if (this.dynamicPriceContext?.preselection?.winterStorage?.id &&
            (this.winterStorages.length === 0 || this.winterStorages.findIndex(w => w.id === this.dynamicPriceContext?.preselection.winterStorage.id) === -1)) {
            this.winterStorages.push(this.dynamicPriceContext?.preselection?.winterStorage);
        }

        this.piers = this.dynamicPriceContext?.preselection?.pier?.id ? [this.dynamicPriceContext?.preselection?.pier] : [];

        if (this.dynamicPriceContext?.preselection?.pier?.id &&
            (this.piers.length === 0 || this.piers.findIndex(p => p.id === this.dynamicPriceContext?.preselection.pier.id) === -1)) {
            this.piers.push(this.dynamicPriceContext?.preselection?.pier);
        }

        if (this.dynamicPrice?.ruleIndex === -1 || !this.dynamicPrice) {
            const preselectedBoat = this.boats.find(b => b.id === this.dynamicPriceContext?.preselection?.boat?.id);
            this.boat = preselectedBoat ? preselectedBoat : ((this.boats.length === 1) ? this.boats[0] : null);

            this._processContextBerth();

            const preselectedTenantRelation = this.tenantRelations.find(b => b.id === this.dynamicPriceContext?.preselection?.tenantRelation?.id);
            this.tenantRelation = preselectedTenantRelation
                ? preselectedTenantRelation
                : ((this.tenantRelations.length === 1) ? this.tenantRelations[0] : null);

            this._processContextSeason();

            const preselectedWinterStorage = this.winterStorages.find(b => b.id === this.dynamicPriceContext?.preselection?.winterStorage?.id);
            this.winterStorage = preselectedWinterStorage ? preselectedWinterStorage : ((this.winterStorages.length === 1) ? this.winterStorages[0] : null);

            const preselectedPier = this.piers.find(p => p.id === this.dynamicPriceContext?.preselection?.pier?.id);
            this.pier = preselectedPier ? preselectedPier : ((this.piers.length === 1) ? this.piers[0] : null);

        } else {
            const {boat, berth, tenantRelation, season, winterStorage, pier} = this.dynamicPrice;

            // Persisted dynamic prices may lack a boat (e.g. created before the boat
            // was set); fall back to the preselected boat from the context.
            this.boat = boat || this.boats.find(b => b.id === this.dynamicPriceContext?.preselection?.boat?.id) || null;
            this.berth = berth;
            this.tenantRelation = tenantRelation;
            this.season = season;
            this.winterStorage = winterStorage;
            this.pier = pier;
        }

    }

    private _processContextBerth() {

        this.berths = (this.berthAssignments || []).map(assignment => assignment.berth) || [];

        if (this.dynamicPriceContext?.preselection?.berth?.id &&
            (this.berths.length === 0 || this.berths.findIndex(b => b.id === this.dynamicPriceContext?.preselection.berth.id) === -1)) {
            this.berths.push(this.dynamicPriceContext?.preselection?.berth);
        }

        const preselectedBerth = this.berths.find(b => b.id === this.dynamicPriceContext?.preselection?.berth?.id);

        this.berth = preselectedBerth ? preselectedBerth : ((this.berths.length === 1) ? this.berths[0] : null);
        this._checkRequirements();
    }

    private _processContextSeason() {
        if (this.seasons?.length > 0) {
            this.season = this._getPreselectedSeason(this.seasons, this.dynamicPriceContext);
            this._checkRequirements();
        } else if (this._seasonsService.seasons?.length > 0) {
            this.seasons = this._seasonsService.seasons;
            this.season = this._getPreselectedSeason(this.seasons, this.dynamicPriceContext);
            this._checkRequirements();
        } else {
            this._seasonsService.getSeasons()
                .pipe(take(1))
                .subscribe(seasons => {
                    this.seasons = seasons;
                    this.season = this._getPreselectedSeason(this.seasons, this.dynamicPriceContext);
                    this._checkRequirements();
                });
        }
    }

    private _getPreselectedSeason(seasons: Season[], dynamicPriceContext: BcmDynamicPriceContext): Season {
        let preselectedSeason = seasons.find(s => s.id === dynamicPriceContext?.preselection?.season?.id);

        if (!preselectedSeason) {
            preselectedSeason = seasons.find(s => s.isDateInSeason(dynamicPriceContext?.fromDate) && s.isDateInSeason(dynamicPriceContext?.toDate));
        }

        return preselectedSeason ? preselectedSeason : null;
    }

    private _setDefaultValues(): void {
        this.boat = ((this.boats.length === 1) ? this.boats[0] : null);
        this.berth = ((this.berths.length === 1) ? this.berths[0] : null);
        this.tenantRelation = ((this.tenantRelations.length === 1) ? this.tenantRelations[0] : null);
        this.season = ((this.seasons.length === 1) ? this.seasons[0] : null);
        this.winterStorage = ((this.winterStorages.length === 1) ? this.winterStorages[0] : null);
        this.pier = ((this.piers.length === 1) ? this.piers[0] : null);
    }

    /**
     * Ob die Bezeichnung noch die automatisch abgeleitete ist. _validateRules() läuft beim Öffnen
     * des Stift-Dialogs und bei jeder Kriterien-/Mengenänderung erneut; eine von Hand vergebene
     * Bezeichnung war danach wieder der Regelname. (#6990)
     */
    private _canOverwriteParentTitle(): boolean {
        const titleControl = this.parentFormGroup?.get('title');

        if (!titleControl) {
            return false;
        }

        const title = titleControl.value;

        return !title
            || title === this.product?.name
            || title === this.dynamicPriceOld?.ruleName
            || title === this._lastAppliedRuleName;
    }

    private async _validateRules(): Promise<void> {
        // Same condition the panel's noRules message is derived from, so the two cannot drift apart.
        if (!this.product || !hasEvaluableRules(this.product)) {
            return;
        }

        const boat = this.boat;
        const berth = this.berth;
        const tenantRelation = this.tenantRelation;
        const season = this.season;
        const winterStorage = this.winterStorage;
        const pier = this.pier;

        const days = getDurationFromDates(
            this.dynamicPriceContext?.fromDate,
            this.dynamicPriceContext?.toDate,
            DurationUnit.Days,
            this.defaultBerthReservationTimeUnit);

        const overnights = getDurationFromDates(
            this.dynamicPriceContext?.fromDate,
            this.dynamicPriceContext?.toDate,
            DurationUnit.Overnights,
            this.defaultBerthReservationTimeUnit);

        const travelers = this.dynamicPriceContext?.travelers || -1;

        // parseInt() truncated the m² quantity before it reached the rule engine - 12,33 m² was
        // evaluated as 12, and an area-priced product bills exactly that number. (#6944)
        const parsedQuantity = Number(this.parentFormGroup.get('quantity')?.value);
        const quantity = Number.isFinite(parsedQuantity) ? parsedQuantity : 0;

        const parameters = {boat, berth, tenantRelation, season, winterStorage, pier, days, overnights, travelers};

        this._productService
            .evaluatePriceRule(
                this.product,
                parameters,
                quantity,
                {
                    width: boat?.width,
                    length: boat?.length
                }
            )
            .pipe(take(1), takeUntil(this._unsubscribeAll))
            .subscribe(dynamicPrice => {

                this._evaluated = true;
                this._evaluationFailed = false;

                this.dynamicPrice = {
                    ...dynamicPrice,
                    id: this.dynamicPriceOld?.id,
                    // A sole boat is auto-selected even when no rule uses it; saved positions take the
                    // dynamic price's boat as their own, so only a boat the rules ask for may ride along. (#7043)
                    boat: this.requirements?.includes('boat') ? this.boat : null,
                    berth: this.berth,
                    tenantRelation: this.tenantRelation,
                    season: this.season,
                    winterStorage: this.winterStorage,
                    pier: this.pier,
                };

                this.formGroup.get('dynamicPrice').setValue(this.dynamicPrice, {emitEvent: false});

                if (this._canOverwriteParentTitle()) {
                    // Ohne Regeltreffer kommt kein Name zurück – ohne Fallback stünde die Position
                    // danach ohne Bezeichnung da.
                    this.parentFormGroup.get('title')?.setValue(this.dynamicPrice.ruleName || this.product?.name);
                    this._lastAppliedRuleName = this.dynamicPrice.ruleName;
                }

                this.parentFormGroup.get('price')?.setValue(this.dynamicPrice.rulePrice);

                this.dynamicPriceChanged.next(this.dynamicPrice);

                if (this.dynamicPriceOld?.id) {
                    this.initIndexChanged = parseFloat(String(this.dynamicPriceOld?.ruleIndex)) !== parseFloat(String(this.dynamicPrice.ruleIndex));
                    this.initTitleChanged = this.dynamicPriceOld?.ruleName !== this.dynamicPrice.ruleName;
                    this.initPriceChanged = parseFloat(String(this.dynamicPriceOld?.rulePrice)) !== parseFloat(String(this.dynamicPrice.rulePrice));
                }
            }, () => {
                // A failed evaluation must still leave the panel saying something rather than a bare
                // toggle - but it is an engine failure, not "no rule applies to this position", so it
                // gets its own message instead of sending the user off to complete missing data. (#6944)
                this._evaluated = true;
                this._evaluationFailed = true;
                this._changeDetectorRef.detectChanges();
            });

    }

}
